import { ChildProviderProps } from "@/types";
import React, {
  useState,
  createContext,
  useContext,
  useCallback,
} from "react";

export interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  checkUnsavedChanges: (action: () => void) => boolean;
  setPendingAction: (action: (() => void) | null) => void;
  pendingAction: (() => void) | null;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
}

export const UnsavedChangesContext = createContext<
  UnsavedChangesContextType | undefined
>(undefined);

export const UnsavedChangesProvider: React.FC<ChildProviderProps> = ({
  children,
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showDialog, setShowDialog] = useState(false);

  const checkUnsavedChanges = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        // Store the action function properly - wrap it to prevent React from calling it
        setPendingAction(() => action);
        setShowDialog(true);
        return false;
      }
      action();
      return true;
    },
    [hasUnsavedChanges]
  );

  return (
    <UnsavedChangesContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        checkUnsavedChanges,
        pendingAction,
        setPendingAction,
        showDialog,
        setShowDialog,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = (): UnsavedChangesContextType => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      "useUnsavedChanges must be used within an UnsavedChangesProvider"
    );
  }
  return context;
};
